import { BLOG_POSTS, BlogCategory, type BlogPostMeta } from "./registry";
import { env } from "@/config/env";
import {
  SEARCH_CONFIG,
  tokenize,
  findTokenMatches,
  calculateFieldScore,
  type TokenMatch,
} from "@/lib/search/fuzzy";

/**
 * Check if a blog post is published (publishedDate is today or in the past)
 * In development mode, all posts are considered published for previewing
 */
export function isPostPublished(post: BlogPostMeta): boolean {
  // In development mode, show all posts (including future-dated ones) for previewing
  if (env.isDevelopment) {
    return true;
  }

  const publishDate = new Date(post.publishedDate + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return publishDate <= today;
}

/**
 * Get only published blog posts
 */
export function getPublishedPosts(posts: BlogPostMeta[]): BlogPostMeta[] {
  return posts.filter(isPostPublished);
}

/**
 * Get all unique categories from blog posts
 */
export function getAllCategories(): BlogCategory[] {
  const categories = new Set<BlogCategory>();
  getPublishedPosts(BLOG_POSTS).forEach((post) => {
    categories.add(post.category);
  });
  return Array.from(categories).sort();
}

/**
 * Get categories sorted by post count (highest first)
 * Optionally filters by search query to only show categories with matching posts
 */
export function getCategoriesByPostCount(searchQuery?: string): BlogCategory[] {
  // If search query provided, filter posts first
  let postsToCount = getPublishedPosts(BLOG_POSTS);

  if (searchQuery?.trim()) {
    // Use the existing filterPosts function to get posts matching the search
    postsToCount = filterPosts({ searchQuery });
  }

  const categoryCounts = new Map<BlogCategory, number>();

  // Count posts per category
  postsToCount.forEach((post) => {
    const count = categoryCounts.get(post.category) || 0;
    categoryCounts.set(post.category, count + 1);
  });

  // Sort by count (descending), then alphabetically for ties
  return Array.from(categoryCounts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) {
        return b[1] - a[1]; // Higher count first
      }
      return a[0].localeCompare(b[0]); // Alphabetical for ties
    })
    .map(([category]) => category);
}

/**
 * Get all unique tags from blog posts
 */
export function getAllTags(): string[] {
  const tags = new Set<string>();
  getPublishedPosts(BLOG_POSTS).forEach((post) => {
    post.tags.forEach((tag) => tags.add(tag));
  });
  return Array.from(tags).sort();
}

/**
 * Blog match information for scoring
 */
interface BlogMatchInfo {
  titleScore: number;
  descriptionScore: number;
  tagScore: number;
  categoryScore: number;
  titleMatches: TokenMatch[];
  descriptionMatches: TokenMatch[];
  tagMatches: TokenMatch[];
  categoryMatches: TokenMatch[];
}

/**
 * Calculate match scores for a blog post
 */
function calculateBlogMatchScores(
  post: BlogPostMeta,
  queryTokens: string[]
): BlogMatchInfo {
  const titleMatches = findTokenMatches(post.title, queryTokens);
  const descriptionMatches = findTokenMatches(post.description, queryTokens);

  // Match against all tags
  const allTagMatches: TokenMatch[] = [];
  for (const tag of post.tags) {
    const tagMatches = findTokenMatches(tag, queryTokens);
    allTagMatches.push(...tagMatches);
  }

  // Match against category
  const categoryMatches = findTokenMatches(post.category, queryTokens);

  // Check if all tokens matched (for AND logic)
  if (SEARCH_CONFIG.requireAllWords) {
    // Collect all matched tokens
    const matchedTokens = new Set<string>();
    titleMatches.forEach((m) => matchedTokens.add(m.token));
    descriptionMatches.forEach((m) => matchedTokens.add(m.token));
    allTagMatches.forEach((m) => matchedTokens.add(m.token));
    categoryMatches.forEach((m) => matchedTokens.add(m.token));

    // All query tokens must have at least one match
    const allTokensMatched = queryTokens.every((token) => matchedTokens.has(token));

    if (!allTokensMatched) {
      return {
        titleScore: 0,
        descriptionScore: 0,
        tagScore: 0,
        categoryScore: 0,
        titleMatches: [],
        descriptionMatches: [],
        tagMatches: [],
        categoryMatches: [],
      };
    }
  } else {
    // OR logic: at least one token must match
    const hasAnyMatch =
      titleMatches.length > 0 ||
      descriptionMatches.length > 0 ||
      allTagMatches.length > 0 ||
      categoryMatches.length > 0;
    if (!hasAnyMatch) {
      return {
        titleScore: 0,
        descriptionScore: 0,
        tagScore: 0,
        categoryScore: 0,
        titleMatches: [],
        descriptionMatches: [],
        tagMatches: [],
        categoryMatches: [],
      };
    }
  }

  // Calculate scores
  const titleScore = calculateFieldScore(titleMatches, post.title);
  const descriptionScore = calculateFieldScore(descriptionMatches, post.description);
  const tagScore = calculateFieldScore(allTagMatches, post.tags.join(" "));
  const categoryScore = calculateFieldScore(categoryMatches, post.category);

  return {
    titleScore,
    descriptionScore,
    tagScore,
    categoryScore,
    titleMatches,
    descriptionMatches,
    tagMatches: allTagMatches,
    categoryMatches,
  };
}

/**
 * Calculate relevance score for a blog post
 */
function calculateBlogPostRelevanceScore(
  post: BlogPostMeta,
  matchInfo: BlogMatchInfo
): number {
  if (
    matchInfo.titleScore === 0 &&
    matchInfo.descriptionScore === 0 &&
    matchInfo.tagScore === 0 &&
    matchInfo.categoryScore === 0
  ) {
    return 0;
  }

  // Apply multipliers
  const titleWeight = matchInfo.titleScore * 2.0; // Title matches are most important
  const descriptionWeight = matchInfo.descriptionScore * 1.0;
  const tagWeight = matchInfo.tagScore * 1.5; // Tag matches are helpful
  const categoryWeight = matchInfo.categoryScore * 1.2; // Category matches are bonus

  return titleWeight + descriptionWeight + tagWeight + categoryWeight;
}

/**
 * Filter blog posts based on category, tags, and search query with fuzzy matching
 */
export function filterPosts(args: {
  category?: string;
  tags?: string[];
  searchQuery?: string;
}): BlogPostMeta[] {
  const { category, tags = [], searchQuery = "" } = args;

  // Start with only published posts
  let filtered = getPublishedPosts(BLOG_POSTS);

  // Filter by category
  if (category) {
    filtered = filtered.filter((post) => post.category === category);
  }

  // Filter by tags (post must have ALL selected tags)
  if (tags.length > 0) {
    filtered = filtered.filter((post) => {
      const postTags = new Set(post.tags);
      return tags.every((tag) => postTags.has(tag));
    });
  }

  // Filter by search query with fuzzy matching
  if (searchQuery.trim()) {
    // Tokenize query
    const queryTokens = tokenize(searchQuery);
    if (queryTokens.length === 0) {
      return filtered;
    }

    // Calculate scores for each post
    const postsWithScores = filtered.map((post) => {
      const matchInfo = calculateBlogMatchScores(post, queryTokens);
      const relevanceScore = calculateBlogPostRelevanceScore(post, matchInfo);

      return {
        post,
        matchInfo,
        relevanceScore,
      };
    });

    // Filter out posts with zero score and sort by relevance
    filtered = postsWithScores
      .filter(({ relevanceScore }) => relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .map(({ post }) => post);
  }

  return filtered;
}

/**
 * Sort blog posts by date (newest first) or title (alphabetical)
 * When posts are already sorted by relevance (from search), this applies secondary sorting
 */
export function sortPosts(
  posts: BlogPostMeta[],
  sortBy: "date" | "title" = "date"
): BlogPostMeta[] {
  const sorted = [...posts];

  // If posts are already sorted by relevance (from filterPosts with search),
  // we only apply secondary sorting for posts with same relevance
  // For now, we'll preserve the order from filterPosts (relevance-sorted)
  // and only re-sort if explicitly needed

  if (sortBy === "date") {
    sorted.sort((a, b) => {
      const dateA = new Date(a.publishedDate).getTime();
      const dateB = new Date(b.publishedDate).getTime();
      return dateB - dateA; // Newest first
    });
  } else if (sortBy === "title") {
    sorted.sort((a, b) => a.title.localeCompare(b.title));
  }

  return sorted;
}
